import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BRAND, addDaysYmd, describeStatus, localParts } from '@rydres/shared';
import { Badge, fmtDateTime, fmtTime, formatUsd } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { useLiveRefresh } from '../lib/live.js';
import { MISSING_LABELS, useProfile } from '../lib/profile.js';
import type { Booking, IncomingReferral } from '../lib/types.js';
import { InstallCard } from './InstallCard.js';
import { ReferralRow } from './Referral.js';
import { Bell } from './Updates.js';

export function BookingRow({ b, tz, timeOnly }: { b: Booking; tz?: string; timeOnly?: boolean }) {
  const look = describeStatus(b);
  return (
    <Link to={`/bookings/${b.id}`} className="block rounded-2xl border border-line bg-card p-4 hover:bg-card2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-bold">{timeOnly ? fmtTime(b.startsAt, tz) : fmtDateTime(b.startsAt, tz)}</div>
          <div className="truncate text-sm text-ink2">{b.riderName} · {b.passengers} pax</div>
          <div className="truncate text-xs text-muted">{b.pickupAddress} → {b.dropoffAddress}</div>
        </div>
        <div className="text-right">
          <div className="font-black">{b.priceCents != null ? formatUsd(b.priceCents) : '—'}</div>
          <Badge value={look.label} tone={look.tone} />
        </div>
      </div>
    </Link>
  );
}

/** The dashboard's full-width buttons: one size and look, close together. */
const ACTION = 'flex w-full items-center justify-between gap-3 rounded-2xl bg-card2 px-4 py-3.5 text-left font-bold';

/** Rides that hold a place in the day, including the ones already driven. */
const SCHEDULED = new Set(['confirmed', 'completed', 'no_show']);

function DaySection({ title, day, rows, tz }: { title: string; day: string; rows: Booking[] | null; tz?: string }) {
  return (
    <section className="space-y-2">
      <Link to={`/bookings?view=calendar&day=${day}`} className={ACTION}>
        {title}
        <span className="flex items-center gap-2 text-muted">
          {rows && rows.length > 0 && <Badge value={String(rows.length)} tone="info" />}›
        </span>
      </Link>
      {rows === null ? (
        <p className="px-1 text-sm text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-1 text-sm text-muted">No appointments scheduled</p>
      ) : (
        rows.map((b) => <BookingRow key={b.id} b={b} tz={tz} timeOnly />)
      )}
    </section>
  );
}

export function Today() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [rows, setRows] = useState<Booking[] | null>(null);
  const [referred, setReferred] = useState<IncomingReferral[]>([]);
  const [copied, setCopied] = useState(false);
  const load = useCallback(() => {
    // From a day back, so the rides already driven today stay on the list.
    const from = new Date(Date.now() - 24 * 3_600_000).toISOString();
    driverApi
      .bookings({ from, status: 'requested,quoted,confirmed,completed,no_show' })
      .then((r) => setRows(r.bookings), () => setRows((x) => x ?? []));
    driverApi.referrals().then((r) => setReferred(r.referrals.filter((x) => x.status === 'waiting_driver')), () => undefined);
  }, []);
  useLiveRefresh(load);

  // Today and tomorrow are the driver's calendar dates, not the phone's.
  const tz = profile?.timezone;
  const zone = tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const todayKey = localParts(new Date(), zone).ymd;
  const tomorrowKey = addDaysYmd(todayKey, 1);
  const dayOf = (b: Booking) => localParts(new Date(b.startsAt), zone).ymd;
  const recent = Date.now() - 6 * 3_600_000;
  const requests =
    rows?.filter((b) => ((b.status === 'requested' && b.paymentMode !== 'card') || b.status === 'quoted') && Date.parse(b.startsAt) >= recent) ?? [];
  const scheduled = rows?.filter((b) => SCHEDULED.has(b.status)) ?? [];
  const later = scheduled.filter((b) => b.status === 'confirmed' && dayOf(b) > tomorrowKey).length;
  const steps = new Set(profile?.check.missing ?? []).size;
  const pageUrl = profile?.handle ? `${BRAND.publicOrigin}/${profile.handle}` : null;

  const share = async () => {
    if (!pageUrl) return;
    if (typeof navigator.share === 'function') {
      await navigator.share({ url: pageUrl }).catch(() => undefined);
      return;
    }
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      navigate('/settings/share'); // no share sheet and no clipboard: the Share screen has the link, text and QR
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Today</h1>
        <div className="flex items-center gap-2">
          <Bell />
          <Link to="/log" className="rounded-xl bg-brand px-3 py-2 text-sm font-bold text-on-brand shadow-soft brand-press">+ Log a trip</Link>
        </div>
      </div>

      <InstallCard />
      <Link to="/services?new=1" className={ACTION}>
        + Add a service <span className="text-muted">›</span>
      </Link>
      {pageUrl ? (
        <button onClick={() => void share()} className="flex w-full items-center justify-between gap-3 rounded-2xl bg-brand px-4 py-4 text-left font-bold text-on-brand shadow-soft brand-press">
          <span className="min-w-0">
            Share your page
            <span className="block truncate text-xs font-medium">{pageUrl.replace(/^https?:\/\//, '')}</span>
          </span>
          <span className="shrink-0 rounded-full border border-current px-3 py-1 text-sm">{copied ? 'Copied' : 'Share'}</span>
        </button>
      ) : (
        <Link to="/settings/profile" className={ACTION}>
          Share your page <span className="text-sm font-medium text-muted">pick a page name ›</span>
        </Link>
      )}
      {profile && !profile.check.live && (
        <Link to="/settings" className="flex items-center justify-between rounded-2xl bg-warn-soft px-4 py-3 text-sm font-bold text-warn">
          {steps} {steps === 1 ? 'step' : 'steps'} before riders can book <span>›</span>
        </Link>
      )}

      {profile?.check.suggested.map((s) => (
        <Link key={s} to={MISSING_LABELS[s]?.to ?? '/settings'} className="flex items-center justify-between rounded-2xl bg-card2 px-4 py-3 text-sm font-bold">
          {MISSING_LABELS[s]?.label} <span className="text-muted">›</span>
        </Link>
      ))}
      {requests.length + referred.length > 0 && (
        <section className="space-y-2">
          <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Requests waiting for you</h2>
          {referred.map((r) => <ReferralRow key={r.id} r={r} tz={tz} />)}
          {requests.map((b) => <BookingRow key={b.id} b={b} tz={tz} />)}
        </section>
      )}
      <DaySection title="Today's appointments" day={todayKey} rows={rows && scheduled.filter((b) => dayOf(b) === todayKey)} tz={tz} />
      <DaySection title="Tomorrow's appointments" day={tomorrowKey} rows={rows && scheduled.filter((b) => dayOf(b) === tomorrowKey)} tz={tz} />
      <Link to="/bookings" className="block rounded-2xl px-4 py-3 text-center text-sm font-bold text-muted">
        All bookings{later > 0 ? ` · ${later} more ${later === 1 ? 'ride' : 'rides'} after tomorrow` : ''}
      </Link>
    </div>
  );
}
